import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu as MenuIcon, X, ExternalLink, Copy, Check } from "lucide-react";
import { restaurantInfo, LOGO_URL, cateringUrl } from "../mock";
import LanguageToggle from "./LanguageToggle";
import { useT } from "../context/LanguageContext";
import { NAV_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const PROMO_CODE = "5OFFLINE";

const navOrder = [
  { key: "home",     to: "/" },
  { key: "menu",     to: "/menu" },
  { key: "catering", href: cateringUrl("navbar"), external: true },
  { key: "contact",  to: "/contact" },
  { key: "about",    to: "/about" },
  { key: "losgatos", to: "/los-gatos", highlight: true },
];

export default function Navbar() {
  const location = useLocation();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const orderClick = (source) => trackEvent("order_online_click", { source });

  const handleCopyPromo = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      trackEvent("promo_code_copy", { code: PROMO_CODE, source: "navbar_mobile" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { /* clipboard blocked */ }
  };

  const OrderBtn = ({ mobile = false }) => (
    <a
      href={restaurantInfo.externalLinks.orderOnline}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        orderClick(mobile ? "navbar_mobile" : "navbar_desktop");
        if (mobile) setOpen(false);
      }}
      data-testid={mobile ? "order-online-btn-mobile" : "order-online-btn-desktop"}
      className={`order-cta relative inline-flex items-center justify-center gap-2 bg-brand text-cream font-semibold rounded-full shadow-md hover:bg-brand-dark hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap ${
        mobile ? "px-5 py-3 text-sm mt-2" : "px-4 xl:px-5 py-2.5 text-[13px] xl:text-sm"
      }`}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cream/70 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-cream" />
      </span>
      {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)}
      <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 py-2.5 ${
        scrolled
          ? "bg-cream/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.07)] md:py-2"
          : "bg-cream/80 backdrop-blur-sm md:py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-3">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Nina's Taqueria — back to home"
          data-testid="navbar-logo-link"
          className="flex items-center gap-2.5 md:gap-3 group shrink-0 min-w-0"
        >
          <div
            className={`relative rounded-full bg-cream border-2 border-brand/25 shadow-md overflow-hidden transition-all duration-300 group-hover:border-brand shrink-0 w-11 h-11 ${
              scrolled ? "md:w-12 md:h-12" : "md:w-14 md:h-14"
            }`}
          >
            <img
              src={LOGO_URL}
              alt="Nina's Taqueria logo"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="leading-none min-w-0">
            <div className="font-display text-base md:text-xl font-semibold text-granite-dark truncate">Nina's Taqueria</div>
            <div className="font-hand text-[12px] md:text-[13px] text-brand -mt-0.5">Auténtico — Los Gatos</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5">
          {navOrder.map((item) =>
            item.external ? (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("catering_click", { source: "navbar_desktop" })}
                data-testid="nav-catering-link"
                className="nav-link whitespace-nowrap text-[12px] xl:text-[12.5px] uppercase tracking-[0.14em] xl:tracking-[0.16em] font-semibold text-granite hover:text-brand transition-colors inline-flex items-center gap-1"
              >
                {t(NAV_STRINGS[item.key].en, NAV_STRINGS[item.key].es)}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            ) : (
            <Link
              key={item.to}
              to={item.to}
              className={`nav-link whitespace-nowrap text-[12px] xl:text-[12.5px] uppercase tracking-[0.14em] xl:tracking-[0.16em] font-semibold transition-colors inline-flex items-center gap-1.5 ${
                item.highlight ? "text-brand hover:text-brand-dark" : "text-granite hover:text-brand"
              } ${location.pathname === item.to ? "active text-brand" : ""}`}
            >
              {item.highlight && (
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-light opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand" />
                </span>
              )}
              {t(NAV_STRINGS[item.key].en, NAV_STRINGS[item.key].es)}
            </Link>
            )
          )}
          <a
            href={restaurantInfo.externalLinks.rewards}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("rewards_click", { source: "navbar" })}
            className="nav-link whitespace-nowrap text-[12px] xl:text-[12.5px] uppercase tracking-[0.14em] xl:tracking-[0.16em] font-semibold text-granite hover:text-brand transition-colors inline-flex items-center gap-1"
          >
            {t(NAV_STRINGS.rewards.en, NAV_STRINGS.rewards.es)}
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <LanguageToggle variant="desktop" />
          </div>
          <div className="hidden sm:block"><OrderBtn /></div>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-cream-100 text-granite"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — slide-from-right drawer with backdrop blur */}
      {/* Backdrop */}
      <button
        aria-label="Close menu"
        onClick={() => setOpen(false)}
        className={`lg:hidden fixed inset-0 z-40 bg-granite-dark/50 backdrop-blur-md transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-backdrop"
      />
      {/* Drawer */}
      <aside
        aria-hidden={!open}
        className={`lg:hidden fixed top-0 right-0 z-50 h-[100dvh] w-[85%] max-w-sm bg-cream shadow-2xl transition-transform duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-cream-100">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative w-9 h-9 rounded-full bg-cream border-2 border-brand/25 shadow overflow-hidden shrink-0">
              <img src={LOGO_URL} alt="" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[15px] font-semibold text-granite-dark truncate">Nina's Taqueria</div>
              <div className="font-hand text-[11px] text-brand -mt-0.5">Auténtico — Los Gatos</div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-cream-100 text-granite shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-1 overflow-y-auto max-h-[calc(100dvh-72px)]">
          <div className="mb-3">
            <LanguageToggle variant="mobile" />
          </div>

          {navOrder.map((item, i) => {
            const active = location.pathname === item.to;
            if (item.external) {
              return (
                <a
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent("catering_click", { source: "navbar_mobile" });
                    setOpen(false);
                  }}
                  data-testid={`mobile-nav-${item.key}`}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className={`group inline-flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl text-granite-dark hover:bg-cream-100 hover:text-brand transition-all ${open ? "mobile-nav-item-in" : ""}`}
                >
                  <span className="font-display text-lg">{t(NAV_STRINGS[item.key].en, NAV_STRINGS[item.key].es)}</span>
                  <ExternalLink className="w-4 h-4 opacity-60" />
                </a>
              );
            }
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.key}`}
                style={{ animationDelay: `${i * 40}ms` }}
                className={`group inline-flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl transition-all ${
                  active
                    ? "bg-brand/10 text-brand"
                    : "text-granite-dark hover:bg-cream-100 hover:text-brand"
                } ${open ? "mobile-nav-item-in" : ""}`}
              >
                <span className="font-display text-lg inline-flex items-center gap-2.5">
                  {item.highlight && (
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-light opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
                    </span>
                  )}
                  {t(NAV_STRINGS[item.key].en, NAV_STRINGS[item.key].es)}
                </span>
                <span className={`text-xs uppercase tracking-widest opacity-40 group-hover:opacity-100 transition-opacity ${active ? "opacity-100" : ""}`}>
                  →
                </span>
              </Link>
            );
          })}

          <a
            href={restaurantInfo.externalLinks.rewards}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent("rewards_click", { source: "navbar_mobile" });
              setOpen(false);
            }}
            style={{ animationDelay: `${navOrder.length * 40}ms` }}
            data-testid="mobile-nav-rewards"
            className={`group inline-flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl text-granite-dark hover:bg-cream-100 hover:text-brand transition-all ${open ? "mobile-nav-item-in" : ""}`}
          >
            <span className="font-display text-lg">{t(NAV_STRINGS.rewards.en, NAV_STRINGS.rewards.es)}</span>
            <ExternalLink className="w-4 h-4 opacity-60" />
          </a>

          <div className="mt-4 pt-4 border-t border-cream-100">
            <OrderBtn mobile />

            {/* Promo code banner — tap to copy */}
            <button
              type="button"
              onClick={handleCopyPromo}
              data-testid="mobile-nav-promo-banner"
              className="mt-3 w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-cream-100 to-brand/10 border-2 border-dashed border-brand/40 hover:border-brand hover:from-brand/10 hover:to-brand/15 transition-all p-3.5 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-tight min-w-0 flex-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-brand-dark font-semibold">
                    {t("Exclusive online offer", "Oferta exclusiva online")}
                  </span>
                  <span className="font-display text-xl font-bold text-brand-dark tracking-[0.12em] mt-0.5">
                    {PROMO_CODE}
                  </span>
                  <span className="text-[11px] text-granite/70 mt-0.5">
                    {t("$5 off your first online order", "$5 de descuento en tu primer pedido")}
                  </span>
                </div>
                <div
                  aria-hidden="true"
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                    copied ? "bg-brand text-cream" : "bg-granite-dark text-cream group-hover:bg-brand"
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </div>
              </div>
              <div className={`text-[11px] mt-1.5 transition-opacity ${copied ? "text-brand-dark font-semibold" : "text-granite/50"}`}>
                {copied
                  ? t("Copied — paste at checkout!", "¡Copiado! Pégalo al pagar")
                  : t("Tap to copy the code", "Toca para copiar el código")}
              </div>
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-xs uppercase tracking-widest text-granite/50">Open every day</div>
            <div className="font-display text-granite-dark text-base mt-0.5">10 AM – 8 PM</div>
            <a
              href={`tel:${restaurantInfo.phone}`}
              className="inline-block mt-2 text-sm text-brand hover:text-brand-dark font-medium"
            >
              {restaurantInfo.phone}
            </a>
          </div>
        </div>
      </aside>
    </header>
  );
}
